import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { SegmentRing } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/segment-ring';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

const RING_SIZE = 96;
const RING_STROKE_WIDTH = 6;

interface Props {
  acceptedCount: number;
  overriddenCount: number;
  rejectedCount: number;
}

export const AgentAllCaughtUpState: React.FC<Props> = ({
  acceptedCount,
  overriddenCount,
  rejectedCount,
}) => {
  const [styles, theme] = useStyles();

  const totalReviewed = acceptedCount + overriddenCount + rejectedCount;

  return (
    <View style={styles.container}>
      <SegmentRing
        acceptedCount={acceptedCount}
        actionedCount={totalReviewed}
        labelVariant="heading-sm"
        overriddenCount={overriddenCount}
        progressPercent={totalReviewed === 0 ? 0 : 100}
        publishedCount={0}
        size={RING_SIZE}
        strokeWidth={RING_STROKE_WIDTH}
      />
      <View style={styles.counts}>
        <Metric
          count={acceptedCount}
          label={i18n.t('pricing.agent.review.status.accepted')}
        />
        <View style={styles.separator} />
        <Metric
          count={rejectedCount}
          label={i18n.t('pricing.agent.review.status.rejected')}
        />
        <View style={styles.separator} />
        <Metric
          count={overriddenCount}
          label={i18n.t('pricing.agent.review.status.overridden')}
        />
      </View>
      <View style={styles.textBlock}>
        <Typography variant="heading-md">
          {i18n.t('pricing.agent.review.all_caught_up.title')}
        </Typography>
        <Typography
          color={theme.sk.color.text.secondary}
          variant="paragraph-md"
        >
          {i18n.t('pricing.agent.review.all_caught_up.subtitle')}
        </Typography>
      </View>
    </View>
  );
};

interface MetricProps {
  count: number;
  label: string;
}

const Metric: React.FC<MetricProps> = ({ count, label }) => {
  const [styles, theme] = useStyles();
  return (
    <View style={styles.metric}>
      <Typography color={theme.sk.color.text.secondary} variant="label-sm">
        {label}
      </Typography>
      <Typography variant="paragraph-md-600">{count}</Typography>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceLarge,
    paddingVertical: theme.sk.spacing.spaceXlarge,
  },
  counts: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceLarge,
  },
  metric: {
    alignItems: 'center',
    gap: 2,
  },
  separator: {
    alignSelf: 'stretch',
    backgroundColor: theme.sk.color.border.primary,
    width: 1,
  },
  textBlock: {
    alignItems: 'center',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
